import { getUTCDateForAnalytics } from "app/utils/utils";

const DATE_KEY_REGEX = /date/i;
const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const YYYY_MM_DD_PREFIX_REGEX = /^(\d{4}-\d{2}-\d{2})/;
const DIGITS_ONLY_REGEX = /^\d+$/;

const isValidDate = d => d instanceof Date && !Number.isNaN(d.getTime());

const toYYYYMMDD = value => {
	if (value instanceof Date) {
		return isValidDate(value) ? getUTCDateForAnalytics(value) : value;
	}

	if (typeof value === "number") {
		// Heuristic: treat as ms timestamp when it looks like one.
		if (value >= 1000000000) {
			return getUTCDateForAnalytics(value);
		}
		return value;
	}

	if (typeof value !== "string") {
		return value;
	}

	const strValue = value.trim();
	if (!strValue) {
		return value;
	}

	if (YYYY_MM_DD_REGEX.test(strValue)) {
		return strValue;
	}

	const prefixMatch = strValue.match(YYYY_MM_DD_PREFIX_REGEX);
	if (prefixMatch?.[1]) {
		return prefixMatch[1];
	}

	// unix timestamps in string form
	if (DIGITS_ONLY_REGEX.test(strValue)) {
		if (strValue.length === 13) {
			return getUTCDateForAnalytics(Number(strValue));
		}
		if (strValue.length === 10) {
			return getUTCDateForAnalytics(Number(strValue) * 1000);
		}
	}

	// Only attempt parsing when it looks date-ish to avoid surprising conversions.
	const looksDateish =
		strValue.includes("-") ||
		strValue.includes("/") ||
		strValue.includes("T") ||
		strValue.includes(":");
	if (!looksDateish) {
		return value;
	}

	const parsed = new Date(strValue);
	if (!isValidDate(parsed)) {
		return value;
	}
	return getUTCDateForAnalytics(parsed);
};

const normalizeDatesFormat = (value, keyHint, seen) => {
	if (value instanceof Date) {
		return toYYYYMMDD(value);
	}

	if (Array.isArray(value)) {
		return value.map(v => normalizeDatesFormat(v, keyHint, seen));
	}

	if (value && typeof value === "object") {
		if (seen.has(value)) {
			return value;
		}
		seen.add(value);

		const out = {};
		Object.entries(value).forEach(([k, v]) => {
			const nextKeyHint = k;
			let nextVal = normalizeDatesFormat(v, nextKeyHint, seen);

			if (DATE_KEY_REGEX.test(k)) {
				nextVal = toYYYYMMDD(nextVal);
			}

			out[k] = nextVal;
		});
		return out;
	}
	if (keyHint && DATE_KEY_REGEX.test(keyHint)) {
		return toYYYYMMDD(value);
	}

	return value;
};

/**
 * Normalize analytics payloads:
 * - If value is a Date => format to "YYYY-MM-DD" (UTC)
 * - If key contains "date" (case-insensitive) and value is date-like => format to "YYYY-MM-DD" (UTC)
 */
export const normalizeAnalyticsTagData = tagData => {
	return normalizeDatesFormat(tagData, undefined, new WeakSet());
};
